import type { ReactNode } from "react";
import { Loader2 } from "lucide-react";
import ToolExamples from "@/components/tools/ToolExamples";
import { buttonVariants } from "@/components/ui/button-variants";
import { cn } from "@/lib/utils";

type ToolLookupFieldProps = {
  id: string;
  name: string;
  label: string;
  placeholder: string;
  submitLabel: string;
  type?: "text" | "email";
  multiline?: boolean;
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  minLength?: number;
  maxLength?: number;
  disabled?: boolean;
  isLoading?: boolean;
  loadingLabel?: string;
  examples?: string[];
  examplesLabel?: string;
  onExampleSelect?: (value: string) => void;
  exampleHrefs?: string[];
  getExampleHref?: (value: string) => string;
  /** Replaces the default submit control (e.g. server-action form with useFormStatus). */
  submitButton?: ReactNode;
  /** Renders before the input in the same row (e.g. phone country code). */
  leadingControl?: ReactNode;
};

export default function ToolLookupField({
  id,
  name,
  label,
  placeholder,
  submitLabel,
  type = "text",
  multiline = false,
  value,
  defaultValue,
  onChange,
  minLength,
  maxLength,
  disabled,
  isLoading = false,
  loadingLabel,
  examples,
  examplesLabel,
  onExampleSelect,
  exampleHrefs,
  getExampleHref,
  submitButton,
  leadingControl,
}: ToolLookupFieldProps) {
  const fieldClassName = cn(
    "w-full rounded-lg border border-line bg-background px-4 py-3 text-sm text-foreground shadow-xs outline-none transition-colors",
    "placeholder:text-muted-foreground focus:border-foreground/25 focus:ring-2 focus:ring-foreground/5",
  );

  return (
    <div className="space-y-2">
      <label htmlFor={id} className="text-sm font-medium text-foreground">
        {label}
      </label>
      <div
        className={cn(
          "flex gap-3",
          leadingControl
            ? "flex-row items-start"
            : "flex-col sm:flex-row sm:items-start",
        )}
      >
        {leadingControl}
        {multiline ? (
          <textarea
            id={id}
            name={name}
            value={value}
            defaultValue={defaultValue}
            onChange={
              onChange ? (event) => onChange(event.target.value) : undefined
            }
            rows={5}
            minLength={minLength}
            maxLength={maxLength}
            required
            disabled={disabled || isLoading}
            placeholder={placeholder}
            className={cn(fieldClassName, "min-h-[140px] resize-y")}
          />
        ) : (
          <input
            id={id}
            type={type}
            name={name}
            value={value}
            defaultValue={defaultValue}
            onChange={
              onChange ? (event) => onChange(event.target.value) : undefined
            }
            minLength={minLength}
            maxLength={maxLength}
            required
            disabled={disabled || isLoading}
            autoComplete="off"
            placeholder={placeholder}
            className={cn(
              fieldClassName,
              "h-12",
              leadingControl && "min-w-0 flex-1",
            )}
          />
        )}
        {submitButton ?? (
          <button
            type="submit"
            disabled={disabled || isLoading}
            aria-busy={isLoading}
            aria-label={isLoading ? (loadingLabel ?? submitLabel) : undefined}
            className={cn(
              buttonVariants({ size: "lg" }),
              "grid h-12 w-full shrink-0 place-items-center px-8 sm:w-auto",
            )}
          >
            <span
              className={cn("col-start-1 row-start-1", isLoading && "invisible")}
            >
              {submitLabel}
            </span>
            {isLoading ? (
              <Loader2
                className="col-start-1 row-start-1 size-5 animate-spin"
                aria-hidden="true"
              />
            ) : null}
          </button>
        )}
      </div>
      {examples && examples.length > 0 && examplesLabel ? (
        <ToolExamples
          label={examplesLabel}
          examples={examples}
          onSelect={onExampleSelect}
          exampleHrefs={exampleHrefs}
          getExampleHref={getExampleHref}
          disabled={disabled || isLoading}
        />
      ) : null}
    </div>
  );
}
